import _ from 'lodash';
import React from 'react';
import {
    MutationFunctionOptions,
    MutationTuple,
    OperationVariables,
    useApolloClient,
} from '/@utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';

export function removeOptimisticData(
    apolloClient: any,
    mutationOptions: MutationFunctionOptions<any, OperationVariables> = {},
) {
    const isOptimisticData = !!mutationOptions?.optimisticResponse;
    const cache = apolloClient.cache;
    const optimisticDataId = cache.optimisticData?.id;
    if (isOptimisticData && optimisticDataId) {
        cache.removeOptimistic(optimisticDataId);
    }
}

/*
 * Wrapper for mutations which allows you to cancel any pending request at any time. It can also be
 * configured to automatically cancel any pending request when making a new one.
 */
export function useCancelableMutation<TData, TVariables>(
    mutationHookResult: MutationTuple<TData, TVariables>,
    newMutationShouldCancelPending = true,
    signal?: AbortSignal,
): [
    MutationTuple<TData, TVariables>[0],
    () => void,
    MutationTuple<TData, TVariables>[1],
] {
    const [mutate, state] = mutationHookResult;
    const apolloClient = useApolloClient();
    const inFlightRequestAbortController = React.useRef<AbortController>(null);
    const pendingPromise = React.useRef<Promise<any>>(null);

    const cancelableMutate = React.useCallback(
        (mutationOptions: MutationFunctionOptions<TData, TVariables> = {}) => {
            if (
                newMutationShouldCancelPending &&
                inFlightRequestAbortController.current
            ) {
                // remove optimistic data layer of the mutation that is going to be aborted
                removeOptimisticData(apolloClient, mutationOptions);

                // abort in-flight request
                inFlightRequestAbortController.current.abort();
                pendingPromise.current?.catch(_.noop);
            }
            inFlightRequestAbortController.current = new window.AbortController();

            pendingPromise.current = mutate({
                ...mutationOptions,
                context: {
                    ...mutationOptions.context,
                    fetchOptions: {
                        // Use signal passed in the param if defined else use signal from newly created abort controller
                        signal:
                            signal ??
                            inFlightRequestAbortController.current.signal,
                    },
                },
            });
            return pendingPromise.current;
        },
        [mutate],
    );

    const cancelMutation = React.useCallback(() => {
        if (inFlightRequestAbortController.current?.abort) {
            inFlightRequestAbortController.current.abort();
            pendingPromise.current?.catch(_.noop);
        }
    }, []);

    return [cancelableMutate, cancelMutation, state];
}
